import React from 'react';
import { Typography, Box, Chip } from "@mui/material";
import { Avatar, Skeleton } from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { format } from "date-fns";

// Shared presentation: callers supply data and existing event handlers.
export default function PatientSummaryHeader({ editedPatient, getCaseOwnerName, isEditing, isMindvaultLinkLoading, isMindvaultLinked, linkedCaseOwnerNames, patient, setCurrentTab }) {
return (<Box
                            sx={{
                              width: '100%',
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '12px',
                              p: 2,
                              mb: 2,
                              cursor: 'pointer',
                              transition: 'border-color 0.2s',
                              '&:hover': { borderColor: '#29C0BB' },
                            }}
                            onClick={() => { if (!isEditing) setCurrentTab('profile'); }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              {/* Profile Picture */}
                              <Avatar
                                sx={{ width: 48, height: 48, backgroundColor: '#E0F7F6', color: '#29C0BB', borderRadius: '10px', fontSize: '1.2rem', fontWeight: 600, flexShrink: 0 }}
                                src={patient.profileImage}
                              >
                                {!patient.profileImage && <AccountCircle sx={{ fontSize: 40 }} />}
                              </Avatar>

                              {/* 3-column info grid */}
                              {/* minmax(0, …): a plain 1fr column will not shrink below its
                                  content, so one long nowrap diagnosis line used to push the
                                  name and case-management columns down to one word per line. */}
                              <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) minmax(0, 2fr) minmax(170px, 1fr)', gap: 2 }}>
                                {/* Column 1: Basic Info */}
                                <Box sx={{ minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>
                                      {isMindvaultLinkLoading
                                        ? <Skeleton variant="text" width={120} sx={{ display: 'inline-block' }} />
                                        : `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim()}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#4B5563', display: 'block' }}>
                                    <Box component="span" sx={{ fontWeight: 500 }}>ID:</Box> {patient.phoneNumber || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#4B5563', display: 'block' }}>
                                    <Box component="span" sx={{ fontWeight: 500, color: '#6B7280' }}>Gender:</Box>{' '}
                                    {patient.gender || 'Not specified'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#4B5563', display: 'block' }}>
                                    <Box component="span" sx={{ fontWeight: 500, color: '#6B7280' }}>Age:</Box>{' '}
                                    {patient.age ? `${patient.age}` : 'Not specified'}
                                  </Typography>
                                </Box>

                                {/* Column 2: Clinical Info */}
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                    <Box component="span" sx={{ fontWeight: 500, color: '#6B7280' }}>Diagnosis:</Box>
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    title={patient.diagnosis && patient.diagnosis.length > 0 ? patient.diagnosis.join(', ') : undefined}
                                    sx={{ color: '#374151', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {patient.diagnosis && patient.diagnosis.length > 0 ? patient.diagnosis.join(', ') : 'None'}
                                  </Typography>
                                  {patient.preExistingConditions && patient.preExistingConditions.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                      {patient.preExistingConditions.slice(0, 3).map((cond) => (
                                        <Chip key={cond} label={cond} size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#F0FDFA', color: '#0D9488', fontWeight: 500 }} />
                                      ))}
                                    </Box>
                                  )}
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 500, color: '#6B7280' }}>
                                    Presenting Concern:
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: '#374151',
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                  }}>
                                    {patient.presenting_concerns || 'None'}
                                  </Typography>
                                </Box>

                                {/* Column 3: Case Management */}
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ color: '#4B5563', display: 'block' }}>
                                    <Box component="span" sx={{ fontWeight: 500, color: '#6B7280' }}>Case Owner:</Box>{' '}
                                    {isMindvaultLinkLoading
                                      ? <Skeleton variant="text" width={80} sx={{ display: 'inline-block' }} />
                                      : isMindvaultLinked
                                        ? (linkedCaseOwnerNames.join(', ') || 'Not assigned')
                                        : ((editedPatient.case_owner || []).map(getCaseOwnerName).filter(Boolean).join(', ') || 'Not assigned')
                                    }
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#4B5563', display: 'block' }}>
                                    <Box component="span" sx={{ fontWeight: 500, color: '#6B7280' }}>Joined:</Box>{' '}
                                    {patient.join_date ? format(new Date(patient.join_date), 'PP') : 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#4B5563', display: 'block', mt: 0.25 }}>
                                    <Box component="span" sx={{ fontWeight: 500, color: '#6B7280' }}>Status:</Box>{' '}
                                    <Chip
                                      label={patient.status || 'Active'}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 500,
                                        backgroundColor: patient.status === 'Inactive' ? '#FEF2F2' : '#F0FDF4',
                                        color: patient.status === 'Inactive' ? '#DC2626' : '#16A34A',
                                      }}
                                    />
                                  </Typography>
                                </Box>
                              </Box>

                            </Box>
                          </Box>);
}
